import React, { useEffect, useState } from 'react';
import { Button, Typography, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../utils/otpHelpers';

/**
 * Resend OTP button with cooldown
 * @param {Object} props
 * @param {Function} props.onResend - Callback when resend is clicked
 * @param {number} props.cooldownSeconds - Initial cooldown in seconds
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.loading - Loading state
 */
const OtpResendButton = ({
    onResend,
    cooldownSeconds = 0,
    disabled = false,
    loading = false
}) => {
    const { t } = useTranslation();
    const [countdown, setCountdown] = useState(cooldownSeconds);

    useEffect(() => {
        setCountdown(cooldownSeconds);
    }, [cooldownSeconds]);

    useEffect(() => {
        if (countdown <= 0) return;

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [countdown]);

    const canResend = countdown === 0 && !disabled && !loading;

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('otp.resend_title')}
            </Typography>

            <Button
                variant="text"
                startIcon={<Refresh />}
                onClick={onResend}
                disabled={!canResend}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                }}
            >
                {countdown > 0
                    ? `${t('otp.resend')} (${formatTime(countdown)})`
                    : t('otp.resend_code')
                }
            </Button>

            {countdown > 0 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    {t('otp.please_wait')} {countdown}s
                </Typography>
            )}
        </Box>
    );
};

export default OtpResendButton;
